import { Suspense, useEffect, type ComponentType } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAssistantScope } from '@rammetec/app-kit/assistant'
import { useAvailableModules, useModuleAccess } from '@vectorhub/application'
import type { AppModuleInstance } from '@vectorhub/domain'
import { ForbiddenPage } from '@/shared/components/ForbiddenPage'
import { GenericModulePage } from '@/shared/components/GenericModulePage'
import { logActivity } from '@/services/logActivity'
import { CurrentModuleProvider } from './CurrentModuleContext'
import { slugToComponent } from './registry'
import { RouteFallback } from '@/shared/components/RouteFallback'

/**
 * Deterministic page-scope anchor for assistant navigation (app.openModule).
 * Must render inside `<Suspense>` next to the lazy page so it mounts in the
 * same commit as screen tools — otherwise `awaitScope` resolves too early.
 */
function ModuleAssistantScope({
  instance,
  title,
}: {
  instance: AppModuleInstance
  title: string
}) {
  useAssistantScope({ id: `module:${instance.id}`, title })
  return null
}

/** Emit `module.open` once per mounted instance (request 0061). */
function ModuleOpenActivity({
  instance,
  label,
}: {
  instance: AppModuleInstance
  label: string
}) {
  useEffect(() => {
    logActivity({
      action: 'module.open',
      module: instance.slug,
      summary: `Abriu módulo ${label}`,
      attrs: { instanceId: instance.id },
    })
  }, [instance.id, instance.slug, label])
  return null
}

export function ModuleEntry() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const { hasAccess, instances } = useModuleAccess(slug)
  const modules = useAvailableModules()

  if (!slug || !hasAccess) {
    return <ForbiddenPage onBack={() => navigate('/')} />
  }

  // VH has exactly one instance per slug (D0009) — so the URL carries only the
  // slug, no instance id.
  const instance = instances[0]
  if (!instance) {
    return <ForbiddenPage onBack={() => navigate('/')} />
  }

  const moduleDef = modules.find((m) => m.slug === slug)
  const Component = (
    slugToComponent[slug as keyof typeof slugToComponent] ?? GenericModulePage
  ) as ComponentType<{ instance: AppModuleInstance }>

  const scopeTitle = instance.label ?? moduleDef?.label ?? instance.slug

  return (
    <CurrentModuleProvider value={moduleDef ?? null}>
      <Suspense fallback={<RouteFallback />}>
        <ModuleAssistantScope instance={instance} title={scopeTitle} />
        <ModuleOpenActivity instance={instance} label={scopeTitle} />
        <Component instance={instance} />
      </Suspense>
    </CurrentModuleProvider>
  )
}
